import { InputControl, SelectControl } from "@shared/components/form";
import { Button } from "@shared/components/ui/Button";
import { ImageUploader } from "@shared/components/ui/ImageUploader";
import { MediaService } from "@shared/services/media.service";
import { MediaFile } from "@shared/types/media.types";
import { useEffect, useState } from "react";
import { FEATURE_FLAGS } from "../../../config/featureFlags";
import { auth } from "../../../config/firebase";

import theme from "@shared/utils/themeColors";
import { useInvitations } from "../hooks/useInvitations";
import { useOrganization } from "../hooks/useOrganization";
import useUserDeletion from "../hooks/useUserDeletion";
import {
	Organization,
	UpdateOrganizationDto,
} from "../types/organization.types";
import DeleteUserModal from "./DeleteUserModal";
import { InvitationForm } from "./InvitationForm";
import { InvitationsList } from "./InvitationsList";

interface OrganizationFormProps {
	initialData?: Partial<Organization>;
}

export default function OrganizationFormExtended({
	initialData,
}: OrganizationFormProps) {
	const { updateOrganization, isUpdating } = useOrganization();
	const {
		invitations,
		loading: invitationsLoading,
		createInvitation,
		deleteInvitation,
		resendInvitation,
		copyInvitationLink,
		fetchInvitations,
	} = useInvitations({
		organizationId: initialData?.id || "",
		onDeleteBlockedByContent: (invitationId) => {
			const invitation = invitations.find((inv) => inv.id === invitationId);
			if (invitation) openDeleteModal(invitation);
		},
	});

	// User deletion hook
	const {
		isLoading: isDeletingUser,
		isModalOpen,
		availableUsers,
		currentInvitation,
		openDeleteModal,
		closeDeleteModal,
		deleteUser,
	} = useUserDeletion({
		onSuccessfulDelete: () => {
			if (currentInvitation) {
				const updatedInvitations = invitations.filter(
					(inv) => inv.id !== currentInvitation.id
				);
				if (initialData?.id) {
					fetchInvitations();
				}
			}
		},
	});

	const [formData, setFormData] = useState<UpdateOrganizationDto>({
		name: initialData?.name || "",
		city: initialData?.city || "",
		postalCode: initialData?.postalCode || "",
		streetAddress: initialData?.streetAddress || "",
		country: initialData?.country || "United States",
		state: initialData?.state || "",
		website: initialData?.website || "",
		size: initialData?.size || "",
		industry: initialData?.industry || "",
		logoUrl: initialData?.logoUrl || "",
	});

	useEffect(() => {
		if (initialData) {
			setFormData({
				name: initialData.name || "",
				city: initialData?.city || "",
				postalCode: initialData?.postalCode || "",
				streetAddress: initialData?.streetAddress || "",
				country: initialData?.country || "United States",
				state: initialData?.state || "",
				website: initialData?.website || "",
				size: initialData?.size || "",
				industry: initialData?.industry || "",
				logoUrl: initialData?.logoUrl || "",
			});
		}
	}, [initialData]);

	// Handle input changes
	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
		>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const [selectedLogo, setSelectedLogo] = useState<MediaFile | null>(null);
	const [logoLoading, setLogoLoading] = useState(false);

	// Handle logo selection
	const handleLogoSelected = async (file: MediaFile) => {
		setSelectedLogo(file);
		setFormData((prev) => ({ ...prev, logoUrl: file.preview }));
	};

	// Handle logo removal
	const handleLogoRemoved = () => {
		setSelectedLogo(null);
		setFormData((prev) => ({ ...prev, logoUrl: "" }));
	};

	// Upload logo to Firebase Storage
	const uploadOrganizationLogo = async (): Promise<string | undefined> => {
		if (!selectedLogo || !auth.currentUser) return undefined;

		try {
			setLogoLoading(true);
			const logoUrl = await MediaService.uploadMedia(
				auth.currentUser.uid,
				selectedLogo,
				{ folder: "organization-logos" },
				(progress) => {
					// Update progress in the UI if needed
					console.log(`Upload progress: ${progress.progress}%`);
				}
			);
			return logoUrl;
		} catch (error) {
			console.error("Error uploading organization logo:", error);
			return undefined;
		} finally {
			setLogoLoading(false);
		}
	};

	// Handle form submission
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		try {
			// Upload logo if selected
			let updatedFormData = { ...formData };

			if (selectedLogo) {
				const logoUrl = await uploadOrganizationLogo();
				if (logoUrl) {
					updatedFormData.logoUrl = logoUrl;
				}
			}

			// Update organization
			updateOrganization(updatedFormData);
		} catch (error) {
			console.error("Error updating organization:", error);
		}
	};

	// List of countries for the dropdown
	const countries = [
		"United States",
		"Canada",
		"Mexico",
		"Brazil",
		"Argentina",
		"Colombia",
		"Chile",
		"Peru",
		"United Kingdom",
		"Germany",
		"France",
		"Spain",
		"Italy",
		"Other",
	];

	// List of US states for the dropdown
	const usStates = [
		"Alabama",
		"Alaska",
		"Arizona",
		"Arkansas",
		"California",
		"Colorado",
		"Connecticut",
		"Delaware",
		"Florida",
		"Georgia",
		"Hawaii",
		"Idaho",
		"Illinois",
		"Indiana",
		"Iowa",
		"Kansas",
		"Kentucky",
		"Louisiana",
		"Maine",
		"Maryland",
		"Massachusetts",
		"Michigan",
		"Minnesota",
		"Mississippi",
		"Missouri",
		"Montana",
		"Nebraska",
		"Nevada",
		"New Hampshire",
		"New Jersey",
		"New Mexico",
		"New York",
		"North Carolina",
		"North Dakota",
		"Ohio",
		"Oklahoma",
		"Oregon",
		"Pennsylvania",
		"Rhode Island",
		"South Carolina",
		"South Dakota",
		"Tennessee",
		"Texas",
		"Utah",
		"Vermont",
		"Virginia",
		"Washington",
		"West Virginia",
		"Wisconsin",
		"Wyoming",
	];

	// List of Canadian provinces for the dropdown
	const canadianProvinces = [
		"Alberta",
		"British Columbia",
		"Manitoba",
		"New Brunswick",
		"Newfoundland and Labrador",
		"Northwest Territories",
		"Nova Scotia",
		"Nunavut",
		"Ontario",
		"Prince Edward Island",
		"Quebec",
		"Saskatchewan",
		"Yukon",
	];

	// Get the appropriate state/province label based on country
	const getStateLabel = () => {
		if (formData.country === "United States") return "State";
		if (formData.country === "Canada") return "Province";
		return "State/Province";
	};

	// Get the appropriate state/province options based on country
	const getStateOptions = () => {
		if (formData.country === "United States") return usStates;
		if (formData.country === "Canada") return canadianProvinces;
		return [];
	};

	return (
		<div className="w-full">
			<div className="p-6 space-y-6">
				<div className="flex justify-between items-center mb-5">
					<h2 className={`text-2xl ${theme.text.primary} font-bold`}>
						Organization Information
					</h2>
				</div>
				<form onSubmit={handleSubmit}>
					<section>
						{/* Organization Logo */}
						<div className="my-5">
							<label
								className={`block text-sm font-medium ${theme.text.primary} mb-3`}
							>
								Organization Logo
							</label>
							<div className="flex items-center">
								<ImageUploader
									roundedClass="rounded-lg"
									className="max-h-[150px] max-w-[300px]"
									imageUrl={formData.logoUrl}
									onImageSelected={handleLogoSelected}
									onImageRemoved={handleLogoRemoved}
									disabled={isUpdating || logoLoading}
									isUploading={logoLoading}
								/>
							</div>
							<p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
								Recommended size: 200x200 pixels or larger.
							</p>
						</div>

						<h2
							className={`text-xl leading-snug ${theme.text.primary} font-bold mb-1`}
						>
							Organization Details
						</h2>
						<div className="text-sm mb-5">
							You can update your organization information here.
						</div>

						<div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
							{/* Organization Name */}
							<div className="sm:w-full">
								<InputControl
									id="name"
									name="name"
									label="Organization/Company Name"
									value={formData.name}
									onChange={(e) => handleChange(e)}
									type="text"
									required
								/>
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
							<div>
								<SelectControl
									id="country"
									name="country"
									label="Country"
									value={formData.country}
									onChange={(e) => handleChange(e)}
									options={countries.map((country) => ({
										value: country,
										label: country,
									}))}
								/>
							</div>

							{(formData.country === "United States" ||
								formData.country === "Canada") && (
									<div>
										<SelectControl
											id="state"
											name="state"
											label={getStateLabel()}
											value={formData.state}
											onChange={(e) => handleChange(e)}
											options={getStateOptions().map((state) => ({
												value: state,
												label: state,
											}))}
											placeholder={`Select ${getStateLabel()}`}
										/>
									</div>
								)}

							{formData.country !== "United States" &&
								formData.country !== "Canada" && (
									<div>
										<InputControl
											id="state"
											name="state"
											label={getStateLabel()}
											value={formData.state}
											onChange={(e) => handleChange(e)}
											placeholder={`Enter ${getStateLabel()}`}
										/>
									</div>
								)}
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
							<div>
								<InputControl
									id="city"
									name="city"
									label="City"
									value={formData.city}
									onChange={(e) => handleChange(e)}
									placeholder="Enter city"
								/>
							</div>

							<div>
								<InputControl
									id="postalCode"
									name="postalCode"
									label="Postal Code"
									value={formData.postalCode}
									onChange={(e) => handleChange(e)}
									placeholder="Enter postal code"
								/>
							</div>
						</div>

						<div className="mt-5">
							<InputControl
								id="streetAddress"
								name="streetAddress"
								label="Street Address"
								value={formData.streetAddress}
								onChange={(e) => handleChange(e)}
								placeholder="Enter street address"
							/>
						</div>

						<div className="mt-5">
							<InputControl
								id="website"
								name="website"
								label="Website"
								value={formData.website}
								onChange={(e) => handleChange(e)}
								type="url"
								placeholder="https://example.com"
							/>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
							<div>
								<SelectControl
									id="size"
									name="size"
									label="Organization Size"
									value={formData.size}
									onChange={(e) => handleChange(e)}
									options={[
										{ value: "1-10", label: "1-10 employees" },
										{ value: "11-50", label: "11-50 employees" },
										{ value: "51-200", label: "51-200 employees" },
										{ value: "201-500", label: "201-500 employees" },
										{ value: "501-1000", label: "501-1000 employees" },
										{ value: "1001+", label: "1001+ employees" },
									]}
									placeholder="Select size"
								/>
							</div>

							<div>
								<SelectControl
									id="industry"
									name="industry"
									label="Industry"
									value={formData.industry}
									onChange={(e) => handleChange(e)}
									options={[
										{ value: "Agriculture", label: "Agriculture" },
										{ value: "Construction", label: "Construction" },
										{ value: "Education", label: "Education" },
										{ value: "Energy", label: "Energy" },
										{ value: "Finance", label: "Finance" },
										{ value: "Healthcare", label: "Healthcare" },
										{ value: "Manufacturing", label: "Manufacturing" },
										{ value: "Retail", label: "Retail" },
										{ value: "Technology", label: "Technology" },
										{ value: "Transportation", label: "Transportation" },
										{ value: "Other", label: "Other" },
									]}
									placeholder="Select industry"
								/>
							</div>
						</div>
					</section>

					{/* Panel footer */}
					<div className={`flex flex-col py-5 mt-6`}>
						<div className="flex self-end">
							<Button type="submit" disabled={isUpdating || logoLoading}>
								{isUpdating
									? "Saving..."
									: logoLoading
										? "Uploading Logo..."
										: "Save Changes"}
							</Button>
						</div>
					</div>
				</form>

				{(FEATURE_FLAGS.SHOW_TEAM_MEMBERS_SECTION || FEATURE_FLAGS.isTeamMembersSectionEnabled(initialData?.id)) && (
					<section className="border-t border-gray-200 dark:border-gray-700 mt-5 pt-8">
						<h2
							className={`text-xl leading-snug ${theme.text.primary} font-bold mb-1`}
						>
							Team Members
						</h2>
						<div className="pt-5">
							<InvitationForm
								onSubmit={createInvitation}
								isLoading={invitationsLoading}
								title="Invite Your Team"
								description="You can invite additional team members from your organization to join the GCCA Membership Hub. Please make sure anyone you invite is currently part of your organization. All invitations are sent to the GCCA team for approval before the invitee receives access. Once approved, the invitation will be sent to your team member within 2 business days."
							/>

							{initialData?.id && (
								<div className="mt-6">
									<InvitationsList
										showApproval
										invitations={invitations}
										onDelete={deleteInvitation}
										onResend={resendInvitation}
										onCopyLink={copyInvitationLink}
										onDeleteUser={(invitationId) => {
											const invitation = invitations.find(
												(inv) => inv.id === invitationId
											);
											if (invitation) {
												openDeleteModal(invitation);
											}
										}}
										isLoading={invitationsLoading || isDeletingUser}
									/>

									<DeleteUserModal
										isOpen={isModalOpen}
										onClose={closeDeleteModal}
										onConfirm={deleteUser}
										isLoading={isDeletingUser}
										availableUsers={availableUsers}
										deletedInvitation={currentInvitation}
									/>
								</div>
							)}
						</div>
					</section>
				)}
			</div>
		</div>
	);
}
